import React, { useState, useEffect, useRef } from 'react';
import {
  CookieIcon, ShieldCheckIcon, ScalesIcon, RocketLaunchIcon,
  SealCheckIcon, HandshakeIcon, HeartIcon, CaretDownIcon,
  ChatCircleDotsIcon, CalculatorIcon, CheckCircleIcon,
  StarIcon, QuotesIcon, CheckIcon, MapPinIcon, ArrowDownIcon
} from '@phosphor-icons/react';
import Logo from './assets/Logo2.webp';

const SectionDivider: React.FC<{ fromColor: string; toColor: string }> = ({ fromColor, toColor }) => (
  <div className="relative h-0 -mt-px" style={{ zIndex: 10 }}>
    <svg
      viewBox="0 0 1440 60"
      preserveAspectRatio="none"
      className="block w-full h-[40px] md:h-[60px]"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="1440" height="1" fill={fromColor} y="0" />
      <path d="M0,0 C480,60 960,60 1440,0 L1440,60 L0,60 Z" fill={toColor} />
    </svg>
  </div>
);

const App: React.FC = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [openMyth, setOpenMyth] = useState<number | null>(null);
  const desktopIframeRef = useRef<HTMLIFrameElement>(null);
  const mobileIframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data === 'expandChat' || (data && data.type === 'expandChat')) {
        setChatOpen(true);
      } else if (data === 'collapseChat' || (data && data.type === 'collapseChat')) {
        setChatOpen(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleCtaClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setChatOpen(true);
    // Tell the intake iframe to open its chat UI (both mobile and desktop)
    desktopIframeRef.current?.contentWindow?.postMessage('expandChat', '*');
    mobileIframeRef.current?.contentWindow?.postMessage('expandChat', '*');
  };

  return (
    <div className="min-h-screen">
      {/* ===== HERO SECTION ===== */}
      <section
        className="hero-texture flex flex-col items-center px-6 md:px-8 pt-16 pb-16 md:pt-20 md:pb-20 text-center relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at top center, #1A426D 0%, #0F2A47 70%)',
        }}
      >
        <div className="relative z-10 flex flex-col items-center">
          <img
            src={Logo}
            alt="Fairmax Law - Debt Relief Law Firm"
            className="w-[200px] md:w-[280px] mb-8 animate-hero-fade-up opacity-0 animation-delay-0"
          />

          <div className="mb-6 animate-hero-fade-up opacity-0 animation-delay-100">
            <CookieIcon
              size={72}
              weight="duotone"
              className="text-[#C0FF70] mx-auto animate-gentle-float drop-shadow-[0_0_15px_rgba(192,255,112,0.3)]"
            />
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 max-w-3xl leading-tight animate-hero-fade-up opacity-0 animation-delay-200">
            Start Your Comeback Story
          </h1>

          <p className="text-lg md:text-xl font-semibold text-[#C0FF70] mb-6 animate-hero-fade-up opacity-0 animation-delay-400">
            You cracked open the right fortune.
          </p>

          <p className="text-base md:text-lg text-white/80 max-w-2xl mb-10 leading-relaxed animate-hero-fade-up opacity-0 animation-delay-600">
            Life threw you a curveball. Debt piled up. But here's the thing &mdash; your story isn't over.
            Fairmax Law helps people across Michigan and Ohio hit reset and start fresh.
            No judgment. No pressure. Just a clear path forward.
          </p>

          <a
            href="#calculator"
            onClick={handleCtaClick}
            className="inline-flex items-center justify-center gap-2 bg-[#C0FF70] text-[#1A426D] font-bold text-lg px-10 py-4 rounded-full
              hover:scale-105 hover:shadow-lg hover:shadow-[#C0FF70]/20 transition-all duration-200
              w-full sm:w-auto max-w-sm animate-hero-fade-up opacity-0 animation-delay-800"
          >
            Try the 2-Min Calculator
            <CaretDownIcon size={20} weight="bold" className="animate-bounce" style={{ animationDuration: '2s' }} />
          </a>
        </div>
      </section>

      <SectionDivider fromColor="#0F2A47" toColor="#1A426D" />

      {/* ===== VALUE PROPOSITIONS ===== */}
      <section className="py-16 md:py-20 px-6 md:px-8 bg-[#1A426D]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#C0FF70] font-semibold text-sm tracking-widest uppercase mb-3 text-center">
            Why Fairmax Law
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-12 max-w-2xl mx-auto">
            Your fortune said it. We make it happen.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Card 1 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 border-t-4 border-t-[#C0FF70] p-6 md:p-8 hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <ShieldCheckIcon size={40} weight="duotone" className="text-[#C0FF70] mb-4" />
              <p className="italic text-[#C0FF70]/60 text-base mb-4 relative pl-5">
                <span className="absolute left-0 top-0 text-[#C0FF70]/30 text-2xl font-serif leading-none">&ldquo;</span>
                A fresh start is closer than you think.
                <span className="text-[#C0FF70]/30 text-lg font-serif leading-none">&rdquo;</span>
              </p>
              <h3 className="text-xl font-bold text-white mb-2">$0 Down to Start</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Begin your journey to debt freedom without any upfront cost.
                Your comeback shouldn't start with another bill.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 border-t-4 border-t-[#C0FF70] p-6 md:p-8 hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <ScalesIcon size={40} weight="duotone" className="text-[#C0FF70] mb-4" />
              <p className="italic text-[#C0FF70]/60 text-base mb-4 relative pl-5">
                <span className="absolute left-0 top-0 text-[#C0FF70]/30 text-2xl font-serif leading-none">&ldquo;</span>
                The weight you carry will soon be lifted.
                <span className="text-[#C0FF70]/30 text-lg font-serif leading-none">&rdquo;</span>
              </p>
              <h3 className="text-xl font-bold text-white mb-2">Stop Garnishments Fast</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Wage garnishments, creditor calls, lawsuits &mdash; we move quickly to put a stop
                to the harassment and give you breathing room.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 border-t-4 border-t-[#C0FF70] p-6 md:p-8 hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <RocketLaunchIcon size={40} weight="duotone" className="text-[#C0FF70] mb-4" />
              <p className="italic text-[#C0FF70]/60 text-base mb-4 relative pl-5">
                <span className="absolute left-0 top-0 text-[#C0FF70]/30 text-2xl font-serif leading-none">&ldquo;</span>
                Your next chapter will be your best.
                <span className="text-[#C0FF70]/30 text-lg font-serif leading-none">&rdquo;</span>
              </p>
              <h3 className="text-xl font-bold text-white mb-2">Wipe Out Debt From Home</h3>
              <p className="text-white/60 text-sm leading-relaxed">
                Our process is streamlined and virtual. File from the comfort of your
                couch anywhere in Michigan or Ohio.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider fromColor="#1A426D" toColor="#0F2A47" />

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-16 md:py-20 px-6 md:px-8 bg-[#0F2A47]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#C0FF70] font-semibold text-sm tracking-widest uppercase mb-3 text-center">
            How It Works
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-3 max-w-2xl mx-auto">
            Three Fortunes. One Fresh Start.
          </h2>
          <p className="text-white/60 text-base md:text-lg text-center mb-12 max-w-xl mx-auto leading-relaxed">
            No office visits. No complicated paperwork. Just three simple steps between you and debt freedom.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Step 1 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:p-8 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <span className="absolute top-4 right-4 text-[#C0FF70]/10 text-7xl font-black leading-none select-none">01</span>
              <CalculatorIcon size={36} weight="duotone" className="text-[#C0FF70] mb-3 relative z-10" />
              <p className="italic text-[#C0FF70]/60 text-sm mb-4 mt-2 relative pl-5 z-10">
                <span className="absolute left-0 -top-1 text-[#C0FF70]/30 text-lg font-serif leading-none">&ldquo;</span>
                The journey of a thousand miles begins with a single click.
                <span className="text-[#C0FF70]/30 text-lg font-serif leading-none">&rdquo;</span>
              </p>
              <h3 className="text-xl font-bold text-white mb-2 relative z-10">Try the 2-Min Calculator</h3>
              <p className="text-white/60 text-sm leading-relaxed relative z-10">
                Answer a few quick questions about your situation. In under two minutes, you'll see
                how much debt you could wipe out. No signup required.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:p-8 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <span className="absolute top-4 right-4 text-[#C0FF70]/10 text-7xl font-black leading-none select-none">02</span>
              <ChatCircleDotsIcon size={36} weight="duotone" className="text-[#C0FF70] mb-3 relative z-10" />
              <p className="italic text-[#C0FF70]/60 text-sm mb-4 mt-2 relative pl-5 z-10">
                <span className="absolute left-0 -top-1 text-[#C0FF70]/30 text-lg font-serif leading-none">&ldquo;</span>
                A wise guide will light the path you cannot yet see.
                <span className="text-[#C0FF70]/30 text-lg font-serif leading-none">&rdquo;</span>
              </p>
              <h3 className="text-xl font-bold text-white mb-2 relative z-10">Talk to Your Attorney</h3>
              <p className="text-white/60 text-sm leading-relaxed relative z-10">
                A licensed Michigan or Ohio bankruptcy attorney reviews your results and walks you
                through your best options. Free, pressure-free, and 100% confidential.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:p-8 relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <span className="absolute top-4 right-4 text-[#C0FF70]/10 text-7xl font-black leading-none select-none">03</span>
              <CheckCircleIcon size={36} weight="duotone" className="text-[#C0FF70] mb-3 relative z-10" />
              <p className="italic text-[#C0FF70]/60 text-sm mb-4 mt-2 relative pl-5 z-10">
                <span className="absolute left-0 -top-1 text-[#C0FF70]/30 text-lg font-serif leading-none">&ldquo;</span>
                The fresh start you seek is already on its way.
                <span className="text-[#C0FF70]/30 text-lg font-serif leading-none">&rdquo;</span>
              </p>
              <h3 className="text-xl font-bold text-white mb-2 relative z-10">File &amp; Move Forward</h3>
              <p className="text-white/60 text-sm leading-relaxed relative z-10">
                We handle the paperwork, the court filings, and the creditor communications.
                Most clients file entirely from home.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <a href="#calculator" onClick={handleCtaClick} className="text-[#C0FF70] font-semibold hover:underline underline-offset-4 transition-all duration-200 inline-flex items-center gap-2">
              Ready for step one?
              <ArrowDownIcon size={18} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      <SectionDivider fromColor="#0F2A47" toColor="#1A426D" />

      {/* ===== COMEBACK STORIES (TESTIMONIALS) ===== */}
      <section className="py-16 md:py-20 px-6 md:px-8 bg-[#1A426D]">
        <div className="max-w-6xl mx-auto">
          <p className="text-[#C0FF70] font-semibold text-sm tracking-widest uppercase mb-3 text-center">
            Comeback Stories
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-3 max-w-2xl mx-auto">
            Fortunes That Came True
          </h2>
          <p className="text-white/60 text-base md:text-lg text-center mb-12 max-w-xl mx-auto leading-relaxed">
            These families cracked open a fortune just like yours. Here's where they are now.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:p-8 relative hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <QuotesIcon size={28} weight="duotone" className="text-[#C0FF70]/20 absolute top-4 right-4" />
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} size={16} weight="fill" className="text-[#C0FF70]" />
                ))}
                <span className="ml-2 text-white/30 text-xs">Google Review</span>
              </div>
              <p className="text-white/80 text-base leading-relaxed italic mb-4">
                &ldquo;Nathalia was very nice, I appreciate her for helping me thru this process.&rdquo;
              </p>
              <p className="text-white/50 text-sm font-semibold mb-4">&mdash; Kristina C.</p>
              <div className="border-t border-white/10 pt-3">
                <p className="text-[#C0FF70]/40 text-xs italic">
                  Fortune: &ldquo;A fresh start is closer than you think.&rdquo;
                </p>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:p-8 relative hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <QuotesIcon size={28} weight="duotone" className="text-[#C0FF70]/20 absolute top-4 right-4" />
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} size={16} weight="fill" className="text-[#C0FF70]" />
                ))}
                <span className="ml-2 text-white/30 text-xs">Google Review</span>
              </div>
              <p className="text-white/80 text-base leading-relaxed italic mb-4">
                &ldquo;Great communication and very professional.&rdquo;
              </p>
              <p className="text-white/50 text-sm font-semibold mb-4">&mdash; Kevin A.</p>
              <div className="border-t border-white/10 pt-3">
                <p className="text-[#C0FF70]/40 text-xs italic">
                  Fortune: &ldquo;The weight you carry will soon be lifted.&rdquo;
                </p>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 md:p-8 relative hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(192,255,112,0.1)] transition-all duration-300">
              <QuotesIcon size={28} weight="duotone" className="text-[#C0FF70]/20 absolute top-4 right-4" />
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} size={16} weight="fill" className="text-[#C0FF70]" />
                ))}
                <span className="ml-2 text-white/30 text-xs">Google Review</span>
              </div>
              <p className="text-white/80 text-base leading-relaxed italic mb-4">
                &ldquo;I want to thank law firm for everything. This was the first bankruptcy I have
                ever done and they were very helpful. Thank you for everything you have done.&rdquo;
              </p>
              <p className="text-white/50 text-sm font-semibold mb-4">&mdash; Bobbi-Jo K.</p>
              <div className="border-t border-white/10 pt-3">
                <p className="text-[#C0FF70]/40 text-xs italic">
                  Fortune: &ldquo;Your next chapter will be your best.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider fromColor="#1A426D" toColor="#0F2A47" />

      {/* ===== FORTUNE BUSTERS (MYTH-BUSTING FAQ) ===== */}
      <section className="py-16 md:py-20 px-6 md:px-8 bg-[#0F2A47]">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#C0FF70] font-semibold text-sm tracking-widest uppercase mb-3 text-center">
            Fortune Busters
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-3">
            Let's Crack Open Some Myths
          </h2>
          <p className="text-white/60 text-base md:text-lg text-center mb-12 max-w-xl mx-auto leading-relaxed">
            Not every fortune tells the truth. Here are the ones we hear most &mdash; and what's really inside.
          </p>

          <div className="space-y-3">
            {[
              {
                myth: 'You will lose everything you own.',
                truth: 'This is the biggest myth out there — and the one that keeps people stuck the longest. In reality, Michigan and Ohio bankruptcy exemptions protect your home, your car, your retirement accounts, and your personal belongings. Most Chapter 7 filers keep everything they have. The law was designed to help you, not punish you.',
              },
              {
                myth: 'Your credit will never recover.',
                truth: "Here's what the fortune didn't tell you: many of our clients see their credit scores start improving within 6–12 months of filing. Why? Because bankruptcy wipes your slate clean, and you can start rebuilding immediately. Some clients have scores in the 700s within two years.",
              },
              {
                myth: 'Everyone will know you filed.',
                truth: "Bankruptcy filings are public record, technically — but nobody is checking. Your employer won't be notified. It won't show up on social media. Your neighbors will never know. In practice, the only people who find out are the people you choose to tell.",
              },
              {
                myth: 'Bankruptcy is for people who gave up.',
                truth: "Filing for bankruptcy is not giving up. It's the opposite — it's choosing to fight for your future instead of drowning in your past. Congress created bankruptcy protection specifically so hardworking people could recover from medical emergencies, job losses, divorces, and other life events.",
              },
              {
                myth: 'It costs too much to file.',
                truth: "At Fairmax Law, you start with $0 down. We also offer a price-match guarantee — if you find a lower price from another bankruptcy firm in Michigan or Ohio, we'll match it. Filing fees can often be waived for qualifying individuals. The real cost isn't filing — it's waiting.",
              },
            ].map((item, index) => (
              <div
                key={index}
                className={`bg-white/5 backdrop-blur-sm rounded-lg border overflow-hidden transition-all duration-300 ${
                  openMyth === index ? 'border-[#C0FF70]/20' : 'border-white/10'
                }`}
              >
                <button
                  onClick={() => setOpenMyth(openMyth === index ? null : index)}
                  className="w-full flex items-center gap-3 p-5 text-left hover:bg-white/5 transition-colors duration-200"
                >
                  <CookieIcon size={20} weight="duotone" className="text-[#C0FF70]/50 shrink-0" />
                  <span className={`font-semibold text-white text-base flex-1 transition-all duration-300 ${
                    openMyth === index ? 'line-through decoration-[#C0FF70]/60 decoration-2' : ''
                  }`}>
                    &ldquo;{item.myth}&rdquo;
                  </span>
                  <CaretDownIcon
                    size={18}
                    weight="bold"
                    className={`text-white/50 shrink-0 transition-transform duration-300 ${
                      openMyth === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    openMyth === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 pb-6 pl-12 pr-8 pt-1">
                    <div className="border-l-2 border-[#C0FF70]/40 pl-5 py-2">
                      <p className="text-white/70 text-sm leading-relaxed flex gap-3">
                        <CheckCircleIcon size={18} weight="duotone" className="text-[#C0FF70] shrink-0 mt-0.5" />
                        <span>{item.truth}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="#calculator" onClick={handleCtaClick} className="text-[#C0FF70] font-semibold hover:underline underline-offset-4 transition-all duration-200">
              Still have questions? The calculator has answers too.
            </a>
          </div>
        </div>
      </section>

      <SectionDivider fromColor="#0F2A47" toColor="#1A426D" />

      {/* ===== YOUR STATE, YOUR PROTECTIONS ===== */}
      <section className="py-16 md:py-20 px-6 md:px-8 bg-[#1A426D]">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#C0FF70] font-semibold text-sm tracking-widest uppercase mb-3 text-center">
            Licensed in Michigan &amp; Ohio
          </p>
          <h2 className="text-2xl md:text-4xl font-bold text-white text-center mb-3 max-w-2xl mx-auto">
            Your State. Your Protections. Your Fresh Start.
          </h2>
          <p className="text-white/60 text-base md:text-lg text-center mb-12 max-w-xl mx-auto leading-relaxed">
            Bankruptcy law varies by state. Here's what the law protects for families in Michigan and Ohio.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Michigan Card */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 border-t-4 border-t-[#C0FF70] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <MapPinIcon size={28} weight="duotone" className="text-[#C0FF70]" />
                <h3 className="text-2xl font-bold text-white">Michigan</h3>
              </div>
              <p className="italic text-[#C0FF70]/50 text-sm mb-6">
                &ldquo;The Great Lakes State has great protections for great people.&rdquo;
              </p>
              <ul className="space-y-4">
                {[
                  { label: 'Home Protection', desc: 'Your home equity is protected under Michigan exemptions' },
                  { label: 'Vehicle Exemption', desc: 'Keep your car — Michigan law protects your vehicle equity' },
                  { label: 'Retirement Accounts', desc: '100% protected — your 401(k) and IRA are completely safe' },
                  { label: 'Wage Protection', desc: 'Stop garnishments the moment you file' },
                  { label: 'Wildcard Exemption', desc: 'Additional protection you can apply to any property you choose' },
                ].map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckIcon size={18} weight="bold" className="text-[#C0FF70] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-white text-sm">{item.label}: </span>
                      <span className="text-white/60 text-sm">{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-[#C0FF70]/50 text-sm italic mt-6">Most Michigan filers keep everything they own.</p>
            </div>

            {/* Ohio Card */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 border-t-4 border-t-[#C0FF70] p-6 md:p-8">
              <div className="flex items-center gap-3 mb-3">
                <MapPinIcon size={28} weight="duotone" className="text-[#C0FF70]" />
                <h3 className="text-2xl font-bold text-white">Ohio</h3>
              </div>
              <p className="italic text-[#C0FF70]/50 text-sm mb-6">
                &ldquo;The Buckeye State believes in second chances.&rdquo;
              </p>
              <ul className="space-y-4">
                {[
                  { label: 'Home Protection', desc: 'Ohio offers some of the strongest homestead exemptions in the Midwest' },
                  { label: 'Vehicle Exemption', desc: 'Keep your car — Ohio protects your vehicle equity' },
                  { label: 'Retirement Accounts', desc: '100% protected — your 401(k) and IRA are untouched' },
                  { label: 'Wage Protection', desc: "Creditors can't touch your paycheck after filing" },
                  { label: 'Personal Property', desc: 'Generous exemptions for furniture, clothing, and appliances' },
                ].map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckIcon size={18} weight="bold" className="text-[#C0FF70] shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-white text-sm">{item.label}: </span>
                      <span className="text-white/60 text-sm">{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-[#C0FF70]/50 text-sm italic mt-6">Ohio believes in second chances — and the law backs it up.</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <a href="#calculator" onClick={handleCtaClick} className="text-[#C0FF70] font-semibold hover:underline underline-offset-4 transition-all duration-200">
              Not sure which exemptions apply to you? The calculator will walk you through it.
            </a>
          </div>
        </div>
      </section>

      <SectionDivider fromColor="#1A426D" toColor="#0F2A47" />

      {/* ===== CTA / CONSULTATION SECTION ===== */}
      <section id="calculator" className="py-20 md:py-28 px-6 md:px-8 bg-[#0F2A47]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <CookieIcon size={20} weight="duotone" className="text-[#C0FF70]" />
            <p className="text-[#C0FF70] font-semibold text-sm tracking-widest uppercase">
              Your Free Consultation
            </p>
          </div>

          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Crack Open Your Fresh Start
          </h2>

          <p className="text-white/70 text-base md:text-lg mb-4 max-w-xl mx-auto leading-relaxed">
            Answer a few quick questions and see how much debt you could eliminate.
            Takes less than 2 minutes. No strings attached.
          </p>

          <p className="text-[#C0FF70] font-semibold text-base md:text-lg mb-3">
            Join 4,000+ Michigan &amp; Ohio families who've gotten a fresh start
          </p>

          <p className="text-white/60 text-sm italic">
            Your comeback starts here. It takes 2 minutes.
          </p>
        </div>
      </section>

      {/* Floating chat widget */}
      {/* Mobile: full-screen iframe */}
      <iframe
        ref={mobileIframeRef}
        src="https://intake.intuidesk.com/?division=6376b82424e2233278fa3571&campaign_id=fortune_cookie&lead_source=69a1f18a5a2f597b2e2829e0"
        title="Debt Relief Chat"
        className={`fixed bottom-0 right-0 w-full border-none outline-none z-50 pointer-events-auto md:hidden ${
          chatOpen ? 'h-full' : 'h-[200px]'
        }`}
        style={{ background: 'transparent' }}
        allow="microphone"
      />
      {/* Desktop: single persistent iframe with CSS-toggled phone frame */}
      <div
        className={`hidden md:block fixed bottom-4 right-4 z-50 pointer-events-auto transition-all duration-300 ease-in-out ${
          chatOpen
            ? 'rounded-[2.5rem] bg-gray-900 p-3'
            : 'rounded-none bg-transparent p-0'
        }`}
        style={
          chatOpen
            ? {
                boxShadow:
                  '0 0 0 2px #1a1a2e, 0 0 0 4px #2d2d44, 0 0 100px 15px rgba(255,255,255,0.15)',
              }
            : undefined
        }
      >
        {/* Notch - always in DOM, visibility toggled */}
        <div
          className={`absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-gray-900 rounded-b-2xl z-10 transition-opacity duration-300 ${
            chatOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        />
        {/* Screen wrapper - always in DOM, styling toggled */}
        <div
          className={`overflow-hidden transition-all duration-300 ${
            chatOpen ? 'rounded-[2rem] ring-1 ring-white/10' : 'rounded-none ring-0'
          }`}
        >
          <iframe
            ref={desktopIframeRef}
            src="https://intake.intuidesk.com/?division=6376b82424e2233278fa3571&campaign_id=fortune_cookie&lead_source=69a1f18a5a2f597b2e2829e0"
            title="Debt Relief Chat"
            className={`border-none outline-none block ${
              chatOpen ? 'w-[375px] h-[680px]' : 'w-[400px] h-[150px]'
            }`}
            style={{ background: 'transparent' }}
            allow="microphone"
          />
        </div>
        {/* Bottom bar - always in DOM, visibility toggled */}
        <div
          className={`flex justify-center transition-all duration-300 ${
            chatOpen ? 'opacity-100 mt-2 mb-1 h-auto' : 'opacity-0 h-0 mt-0 mb-0 overflow-hidden'
          }`}
        >
          <div className="w-28 h-1 rounded-full bg-gray-600" />
        </div>
      </div>

      <SectionDivider fromColor="#0F2A47" toColor="#1A426D" />

      {/* ===== TRUST STRIP ===== */}
      <section className="py-14 md:py-20 px-6 md:px-8 bg-[#1A426D]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="flex flex-col items-center text-center p-6 rounded-xl bg-white/5 border border-white/10">
            <SealCheckIcon size={44} weight="duotone" className="text-[#C0FF70] mb-3" />
            <p className="text-white font-bold text-xl">Licensed in MI & OH</p>
            <p className="text-white/60 text-sm">Board-certified bankruptcy attorneys</p>
          </div>

          <div className="flex flex-col items-center text-center p-6 rounded-xl bg-white/5 border border-white/10">
            <HandshakeIcon size={44} weight="duotone" className="text-[#C0FF70] mb-3" />
            <p className="text-white font-bold text-xl">Price Match Guarantee</p>
            <p className="text-white/60 text-sm">We'll match any competitor's price</p>
          </div>

          <div className="flex flex-col items-center text-center p-6 rounded-xl bg-white/5 border border-white/10">
            <HeartIcon size={44} weight="duotone" className="text-[#C0FF70] mb-3" />
            <p className="text-white font-bold text-xl">4,000+ Families Helped</p>
            <p className="text-white/60 text-sm">Trusted across the Midwest</p>
          </div>
        </div>
      </section>

      <SectionDivider fromColor="#1A426D" toColor="#0B1F35" />

      {/* ===== FINAL FORTUNE (CLOSING CTA) ===== */}
      <section
        className="py-20 md:py-28 px-6 md:px-8 relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(192,255,112,0.04) 0%, transparent 70%), #0B1F35',
        }}
      >
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <CookieIcon
            size={48}
            weight="duotone"
            className="text-[#C0FF70]/30 mx-auto mb-6 animate-gentle-float"
          />

          <div className="relative mb-8 px-8">
            <span className="absolute -left-2 top-0 text-5xl text-[#C0FF70]/15 font-serif leading-none select-none">&ldquo;</span>
            <p className="text-xl md:text-2xl italic text-[#C0FF70]/70 leading-relaxed">
              The best time to start over was yesterday. The second best time is right now.
            </p>
            <span className="absolute -right-2 bottom-0 text-5xl text-[#C0FF70]/15 font-serif leading-none select-none">&rdquo;</span>
          </div>

          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Your Comeback Story Starts in 2 Minutes
          </h2>

          <p className="text-white/70 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            You've read the fortunes. You've seen the proof. You know the truth about bankruptcy.
            Now there's only one question left: are you ready to take the first step?
            Our free calculator will show you exactly where you stand &mdash; no commitment, no judgment, no cost.
          </p>

          <a
            href="#calculator"
            onClick={handleCtaClick}
            className="inline-flex items-center justify-center gap-2 bg-[#C0FF70] text-[#1A426D] font-bold text-lg px-10 py-4 rounded-full
              hover:scale-105 transition-all duration-200
              w-full sm:w-auto max-w-sm"
          >
            Crack Open Your Fresh Start
            <ArrowDownIcon size={20} weight="bold" />
          </a>

          <p className="text-white/50 text-sm mt-4">
            Takes less than 2 minutes. 100% free. 100% confidential.
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-16 md:py-20 px-6 md:px-8 bg-[#1A426D] text-center relative z-20">
        <div className="max-w-5xl mx-auto">
          <img
            src={Logo}
            alt="Fairmax Law"
            className="w-[200px] md:w-[240px] mx-auto mb-6 object-contain"
          />

          <p className="text-white/50 text-sm mb-4">
            We are a debt relief agency. We help people file for bankruptcy relief under the Bankruptcy Code.
          </p>

          <p className="text-white/40 text-xs mb-4">
            This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
          </p>

          <p className="text-white/40 text-xs mb-4">
            All legal services are provided by in house employees or affiliated and related entities. Please contact us if you have specific questions on a state or entity.
          </p>

          <p className="text-white/40 text-xs mb-4">
            Fairmax Law PLLC is a law firm.
          </p>

          <p className="text-white/35 text-[11px] mb-4 leading-relaxed">
            PAID ATTORNEY ADVERTISEMENT: Fairmax Law PLLC is a law firm. This website is an advertisement. It is not a lawyer referral service or prepaid legal services plan. Each of the lawyers on this site are either Managers, Members, Partners, Of Counsel, Limited Partners or staff attorneys. Some photos are of models and do not depict clients. The attorney(s) responsible for the content of this Site are listed above in our directory of lawyers.
            Attorney David Ienna is the Chief Executive of the firm in all states. Prior results do not guarantee a similar outcome.
          </p>

          <p className="text-white/35 text-[11px] mb-4 leading-relaxed">
            If you live in Alabama, Florida, Missouri, New York, or Wyoming, please click here for additional information. By an Act of Congress and the President of the United States, FairmaxLaw, a service of Fairmax Law is a federally designated Debt Relief Agency. Attorneys and/or law firms promoted through this website are also federally designated Debt Relief Agencies. They help people file for relief under the U.S Bankruptcy Code. Disclosures Required Under the U.S Bankruptcy Code.
          </p>

          <p className="text-white/40 text-xs mb-8">
            If you wish to contact us without opting in to receive automatic texts and phone calls, call us at 888-FAIRMAX and press 9.
          </p>


          <p className="text-white/30 text-xs">
            &copy; {new Date().getFullYear()} Fairmax Law. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
